import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'react-apollo';
import { connect } from 'react-redux';
import Modal from 'chameleon/ui-stack/ui/base/modal';
import Header from 'chameleon/ui-stack/ui/page/header';
import HorizontalTabs from 'chameleon/ui-stack/ui/base/tabs/horizontal-tabs';
import searchResources from 'legacy-src/actions/searchResources';
import { constants } from 'chameleon/ui-stack/ui/units/utils';
import { unitsClient } from 'legacy-src/utility/GraphqlClients';
import { connectLean } from 'lean-redux';
import ResourceTypes from 'legacy-packages/unit-outline/src/constants';
import { COURSE_GUIDE_LABEL } from 'chameleon-src/components/SidebarWrapperTwoLevels/constants';
import Breadcrumbs from '../Breadcrumbs';
import ResultsTable from '../ResultsTable';
import { SEARCH_RESOURCE_TYPES_MAP, getFilters } from '../utils/constants';
import SearchControl from '../SearchControl';
import HelpAndTips from '../HelpAndTips';

const Fauxdal = (props) => {
  const {
    router,
    pushParamsToHistory,
    queryParams,
    isStudent,
    outlineSearchInstructionalResourceSearchText,
    onSearch,
    getResources,
    resources,
    isLoadingResources,
    educationPeriod,
    unitNumber,
    units,
    isPreApSubject,
    widgetTagColumn,
    widgetTagColumnLabel,
    unitsLabel,
    teacherAssignments,
    loadingAssignments,
    courseOutline,
    unitsState,
    studentPracticeProblems,
    subunitLabel,
    apSourcesFlag,
    searchTerm,
    setSearchTerm,
  } = props;
  const {
    params: { subject: subjectId },
  } = router;
  const {
    searchType: resourceType = '',
    searchTerm: querySearchTerm = '',
    sort: sortBy = 'default_sort',
  } = queryParams || {};
  const { searchResultsTags } = courseOutline;
  const resourcesLength = resources.length;
  const [resourceFilters, setResourceFilters] = useState([]);
  const { TABLE_SORT_MAP } = constants;

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    setResourceFilters(
      getFilters(
        courseOutline,
        studentPracticeProblems,
        apSourcesFlag,
        isPreApSubject,
      ),
    );

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isPreApSubject]);

  useEffect(() => {
    getResources({
      resourceTypes: resourceType
        .split(',')
        .map((rt) => SEARCH_RESOURCE_TYPES_MAP[rt])
        .filter(Boolean),
      resourceSubType: null,
      subjectId,
      unitsClient,
      searchTerm: querySearchTerm || '',
      educationPeriod: educationPeriod
        ? Number.parseInt(educationPeriod, 10)
        : '',
      sortOrder: TABLE_SORT_MAP[sortBy] || 'default_sort',
    });
  }, [querySearchTerm, resourceType, sortBy]);

  const close = () => {
    router.push(`/${subjectId}/home?unit=${queryParams.unit}`);
  };

  return (
    <Modal
      title={<Breadcrumbs subjectId={subjectId} unitNumber={unitNumber} />}
      size="full"
      onClose={close}
    >
      <Fragment>
        <div className="px-4.6 py-4.2">
          <HelpAndTips subjectId={subjectId} />
          <Header subtitle={`${COURSE_GUIDE_LABEL} Search`}>
            <div className="flex flex-col w-256 mt-4 justify-center self-center">
              <div className="flex items-center justify-center w-100 gap-x-6">
                <SearchControl
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  onSearch={onSearch}
                  pushParamsToHistory={pushParamsToHistory}
                  isStudent={isStudent}
                  outlineSearchInstructionalResourceSearchText={
                    outlineSearchInstructionalResourceSearchText
                  }
                />
              </div>
              <div className="mt-4.2 text-gray-600 text-12 text-left">
                {resourcesLength} resource
                {resourcesLength !== 1 ? 's' : ''} found
              </div>
            </div>
          </Header>
        </div>
        <div className="px-4.6 border-b border-gray-300">
          <HorizontalTabs
            tabs={resourceFilters.map((filter) => ({
              name: filter.name,
              onClick: () => pushParamsToHistory({ searchType: filter.value }),
              isActive: filter.value === resourceType,
            }))}
          />
        </div>
        <div className="px-4.6 py-4.2 mt-4.2">
          <ResultsTable
            resources={resources}
            isLoading={isLoadingResources}
            pushParamsToHistory={pushParamsToHistory}
            units={units}
            isPreApSubject={isPreApSubject}
            subjectId={subjectId}
            widgetTagColumn={widgetTagColumn}
            widgetTagColumnLabel={widgetTagColumnLabel}
            unitsLabel={unitsLabel}
            router={router}
            searchTerm={searchTerm}
            searchType={resourceType}
            sortBy={sortBy}
            teacherAssignments={teacherAssignments}
            loadingAssignments={loadingAssignments}
            searchResultsTags={searchResultsTags}
            unitsState={unitsState}
            subunitLabel={subunitLabel}
          />
        </div>
      </Fragment>
    </Modal>
  );
};

Fauxdal.propTypes = {
  router: PropTypes.object.isRequired,
  pushParamsToHistory: PropTypes.func.isRequired,
  queryParams: PropTypes.object.isRequired,
  isStudent: PropTypes.bool.isRequired,
  outlineSearchInstructionalResourceSearchText: PropTypes.bool,
  onSearch: PropTypes.func,
  resources: PropTypes.array,
  isLoadingResources: PropTypes.bool,
  getResources: PropTypes.func.isRequired,
  educationPeriod: PropTypes.string,
  unitNumber: PropTypes.string.isRequired,
  teacherAssignments: PropTypes.object,
  loadingAssignments: PropTypes.bool,
  courseOutline: PropTypes.object.isRequired,
  subunitLabel: PropTypes.string,
};

Fauxdal.defaultProps = {
  outlineSearchInstructionalResourceSearchText: false,
  onSearch: () => {},
  resources: [],
  isLoadingResources: true,
  educationPeriod: null,
  teacherAssignments: {},
  loadingAssignments: false,
  subunitLabel: null,
};

const mapStateToProps = ({
  searchedResources: { resources = [], isFetching } = {},
  featureFlag: {
    student_practice_problems,
    spp_filter_by_subject,
    spp_filter_by_school,
    ap_sources,
  },
}) => ({
  // Filter out Sources if ap_sources feature flag is off
  resources: resources.filter(
    ({ resource }) =>
      ap_sources || resource.__typename !== ResourceTypes.SOURCE,
  ),
  isLoadingResources: isFetching,
  studentPracticeProblems:
    student_practice_problems ||
    (spp_filter_by_subject && spp_filter_by_school),
  apSourcesFlag: ap_sources,
});

export default compose(
  connectLean({
    scope: 'units',
    mapState: (state) => ({
      unitsState: state,
    }),
  }),
  connect(mapStateToProps, {
    getResources: searchResources,
  }),
)(Fauxdal);
